import {useStateRef} from '@/pages/ISWAN_CHAT/hooks/useStateRef/useStateRef';
import {MessageItem} from '@/pages/ISWAN_CHAT/interface';
import {DecodedResponseChunk, decodeResponseChunk,} from '@/pages/ISWAN_CHAT/utils/decodeResponseChunk';
import {parseMsgFormXChatMsg, parseMsgFromDifyResponse} from '@/pages/ISWAN_CHAT/utils/parseMsg';
import {findMessagesFromRemote, stopResponse} from '@/services/AiService';
import {useXAgent, useXChat, XRequest} from '@ant-design/x';
import {MessageStatus} from '@ant-design/x/es/use-x-chat';
import {useModel} from '@umijs/max';
import {useEffect, useRef, useState} from 'react';
import BASEURLCONFIG from '../../../../../config/BASEURLCONFIG';

export interface QueryExtra {
  deepseek?: '启用';
}

export interface ChatHook {
  key: string;
  event: 'onSuccess' | 'onError' | 'onUpdate';
  fn: (e: any) => void;
}

export const useChat = () => {
  const {initialState} = useModel('@@initialState');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [aiType, _, aiTypeRef] = useModel('ISWAN_CHAT.aiType');
  const [conversationId, setConversationId, conversationIdRef] = useStateRef<string>('');
  const messagesRef = useRef<MessageItem[]>([]);
  const hookFnRef = useRef<ChatHook[]>([]);
  const [rerender, setRerender] = useState(1);
  const extraQueryRef = useRef<QueryExtra | undefined>();
  const taskIdRef = useRef<string | undefined>();

  const {create} = XRequest({
    baseURL: BASEURLCONFIG.ISWAN_AI_URL + '/api/ai/v1/chat-messages/sse',
    // dangerouslyApiKey: 'sk-5FiOTOoEoVXaSQQ2BzY0CgTBhYrFKIpmFHgtxXrER0M0j7PJ',
    // model: 'gpt-4o-mini',

    // dangerouslyApiKey: 'Bearer sk-rjwnohtmhexsajxzxflaeqedgvtvdscttkaifkowyptybpcn',
    // model: 'deepseek-ai/DeepSeek-V3',

    // dangerouslyApiKey: 'Bearer sk-rjwnohtmhexsajxzxflaeqedgvtvdscttkaifkowyptybpcn',
    // model: 'deepseek-ai/DeepSeek-R1',
  });

  const [agent] = useXAgent<DecodedResponseChunk[]>({
    request: async (info, cb) => {
      const {onUpdate, onSuccess} = cb;
      const {message} = info;
      const getQuery = () => {
        if (message === undefined || message?.length === 0) {
          return '';
        }
        return message[0].type === 'message' ? message[0].text : '';
      };

      const chunkDataList: DecodedResponseChunk[] = [];

      try {
        create(
          {
            inputs: extraQueryRef.current,
            query: getQuery(),
            conversation_id: conversationIdRef.current,
            user: initialState?.currentUser?.mobile ?? 'anonymous_maybeError_web',
            $ai_type: aiTypeRef.current,
          },
          {
            onSuccess: (chunks) => {
              console.log('sse chunk list', chunks);
              hookFnRef.current
                .filter((item) => item.event === 'onSuccess')
                .forEach((item) => item.fn(chunks));
            },
            onError: (error) => {
              console.log('error', error);
              hookFnRef.current
                .filter((item) => item.event !== 'onError')
                .forEach((item) => item.fn(error));
            },
            onUpdate: (chunk) => {
              console.log('sse object', chunk);
              hookFnRef.current
                .filter((item) => item.event === 'onUpdate')
                .forEach((item) => item.fn(chunk));

              const answerData = decodeResponseChunk(chunk.data);
              chunkDataList.push(answerData);
              const cIdChunk = chunkDataList.find((e) => !!e.conversationId);

              if (answerData.end) {
                onSuccess(chunkDataList);
              } else {
                onUpdate(chunkDataList);
              }

              if (!conversationIdRef.current) {
                setConversationId(cIdChunk === undefined ? '' : cIdChunk.conversationId);
              }

              if (!!answerData.taskId) {
                taskIdRef.current = answerData.taskId;
              }
            },
          },
        );
      } catch (error) {
        // handle error
      }
    },
  });

  const {
    onRequest,
    // parsedMessages: xChatMsgs,
    messages: xChatMsgs,
    setMessages,
  } = useXChat<DecodedResponseChunk[]>({
    agent,
    requestPlaceholder: [
      {type: 'message', text: '正在思考中...', conversationId: '', taskId: ''},
    ],
    requestFallback: [
      {type: 'message', text: '服务升级中，请稍后再试', conversationId: '', taskId: ''},
    ],
  });

  useEffect(() => {
    messagesRef.current = parseMsgFormXChatMsg(xChatMsgs);
    setRerender((prev) => prev + 1);
    console.log(rerender);
  }, [xChatMsgs]);

  const send = (content: string, extraQuery?: QueryExtra) => {
    extraQueryRef.current = extraQuery;
    onRequest([{type: 'message', text: content, conversationId: '', taskId: ''}]);
  };

  const stop = () => {
    if (!taskIdRef.current) {
      return;
    }
    return stopResponse({
      $ai_type: aiTypeRef.current,
      taskId: taskIdRef.current,
      user: initialState?.currentUser?.mobile ?? 'anonymous_maybeError_web',
    });
  };

  const addHookFn = (hook: ChatHook) => {
    hookFnRef.current = hookFnRef.current.filter((item) => item.key !== hook.key);
    hookFnRef.current = [...hookFnRef.current, hook];
  };

  const removeHookFn = (key: string) => {
    hookFnRef.current = hookFnRef.current.filter((item) => item.key !== key);
  };

  const removeAllHookFn = (event?: ChatHook['event']) => {
    if (event) {
      hookFnRef.current = hookFnRef.current.filter((item) => item.event !== event);
      return;
    }
    hookFnRef.current = [];
  };

  const updateMessages = (messages: MessageItem[], cleanCId = false) => {
    if (cleanCId) {
      setConversationId('');
    }

    setMessages(() => {
      const updatedMessages: {
        id: string | number;
        message: DecodedResponseChunk[];
        status: MessageStatus;
      }[] = [];
      messages.forEach((item) => {
        if (item.role === 'system') {
          return;
        }
        if (item.role === 'user') {
          updatedMessages.push({
            id: item.key,
            message: item.content,
            status: 'local',
          });
        }
        if (item.role === 'assistant') {
          updatedMessages.push({
            id: item.key,
            message: item.content,
            status: 'success',
          });
        }
      });

      return updatedMessages;
    });
  };

  const emptyChat = () => {
    setConversationId('');
    taskIdRef.current = undefined;
    updateMessages([]);
    messagesRef.current = [];
  };

  const handleSetConversationId = async (id?: string) => {
    if (id === undefined || id === '') {
      emptyChat();
      return;
    }
    setConversationId(id);
    if (aiType !== null) {
      const res = await findMessagesFromRemote({
        conversation_id: id,
        $ai_type: aiType,
        limit: 100,
      });
      updateMessages(parseMsgFromDifyResponse(res.data));
    }
  };

  useEffect(() => {
    stop();
    emptyChat();
    const fetchData = async () => {
      // let resForParameters = await getParameters({$ai_type:aiType});
      // console.log(resForParameters,'resForParameters');
      // let resForWebApp = await getSite({$ai_type:aiType});
      // console.log(resForWebApp,'resForWebApp');
    };
    fetchData();
  }, [aiType]);

  return {
    conversationId,
    setConversationId: handleSetConversationId,
    messages: messagesRef.current,
    setMessages: updateMessages,
    messagesRef: messagesRef,
    send,
    stop,
    addHookFn,
    removeHookFn,
    removeAllHookFn,
    loading: agent.isRequesting(),
  };
};
